"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import { ResponsiveDialog } from "@repo/ui/components/responsive-dialog";
import { cs, enUS, pl } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAddonsConfigStore } from "@/features/addons/components/addons-config-provider";
import { useDietAddonModal } from "@/features/addons/hooks/use-diet-addon-modal";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { useAddonRemoveFromCartEvent } from "@/features/analytics/hooks/use-addon-remove-from-cart-event";
import { useAddonViewItemEvent } from "@/features/analytics/hooks/use-addon-view-item-event";
import { getLocalePrice } from "@/lib/utilities";
import { DietAddonEditorBody } from "./diet-addon-editor-body";

const dateFnsLocales = { pl, en: enUS, cz: cs };

type DietAddonModalProps = {
  addon: AddonDietAddonsRead;
  dietDates: Date[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommitted?: () => void;
  cateringName?: string;
  categoryName?: string;
  // Addon quote of the position being edited, used to report the price the
  // cart charges when an empty edit drops the addon (MPL-3873).
  subItemsPricing?: SubItemsPricing | null;
};

export const DietAddonModal = ({
  addon,
  dietDates,
  open,
  onOpenChange,
  onCommitted,
  cateringName,
  categoryName,
  subItemsPricing,
}: DietAddonModalProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const calendarLocale =
    dateFnsLocales[locale as keyof typeof dateFnsLocales] ?? pl;

  const getAddonEntries = useAddonsConfigStore(
    (store) => store.getAddonEntries,
  );
  const setAddonEntries = useAddonsConfigStore(
    (store) => store.setAddonEntries,
  );

  // Snapshot the committed entries once for this open instance.
  const initialEntries = useMemo(
    () => getAddonEntries(addon.id),
    [addon.id, getAddonEntries],
  );

  const {
    allowedDates,
    allowedKeys,
    selectedDates,
    selection,
    totalQuantity,
    canAddMore,
    isAllSelected,
    perUnitPrice,
    totalPrice,
    isEditing,
    canCommit,
    isRemoval,
    syncSelectedDates,
    incrementDay,
    decrementDay,
    removeDay,
    toggleAll,
    buildEntries,
  } = useDietAddonModal({ addon, dietDates, initialEntries });

  useAddonViewItemEvent({
    addon,
    cateringName,
    categoryName,
    enabled: open,
    selectedDates,
    totalQuantity,
  });

  const reportRemovedAddon = useAddonRemoveFromCartEvent();

  const handleSubmit = () => {
    // Only adding needs at least one day. The button stays enabled at zero so
    // the user gets an explicit reason instead of a dead button.
    if (!(isEditing || totalQuantity > 0)) {
      toast.error(t("cart.configuration.addons.modal.noDaysSelected"));
      return;
    }
    if (!canCommit) {
      return;
    }
    if (isRemoval) {
      // Confirming an empty edit drops the addon from the selection, so report
      // it here — abandoning the editor changes nothing and is not reported.
      reportRemovedAddon({
        addon,
        cateringName,
        categoryName,
        entries: initialEntries,
        pricing: subItemsPricing,
      });
    }
    // Committing writes to the shared store, which (in the edit flow) triggers
    // the PUT /cart/{id} update on the configuration page.
    setAddonEntries(addon.id, buildEntries());
    if (onCommitted) {
      onCommitted();
    } else {
      onOpenChange(false);
    }
  };

  const footer = (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex w-full min-w-0 flex-col">
        <span className="font-bold text-brand-violet text-xl leading-7">
          {getLocalePrice({ price: totalPrice })}
        </span>
        <span className="text-brand-green2-300 text-sm leading-[22px]">
          {t("cart.configuration.addons.modal.summary", {
            count: totalQuantity,
            price: getLocalePrice({ price: perUnitPrice }),
          })}
        </span>
      </div>
      <Button
        className="h-auto rounded-xl bg-brand-yellow px-8 py-4 font-medium text-base text-brand-green2-300 hover:bg-brand-yellow-hover disabled:opacity-40"
        onClick={handleSubmit}
        type="button"
      >
        {isEditing
          ? t("cart.configuration.addons.modal.confirm")
          : t("cart.configuration.addons.modal.submit")}
      </Button>
    </div>
  );

  return (
    <ResponsiveDialog
      contentClassName="flex flex-col gap-6 bg-brand-grey1 p-4 md:max-h-[75vh] md:overflow-y-auto md:p-6"
      dialogClassName="gap-0 overflow-hidden p-0 md:w-[calc(100%-2rem)] md:max-w-[1080px] max-h-[775px] md:h-[775px]"
      footer={footer}
      footerClassName="border-t bg-brand-white px-4 py-4 md:px-6 w-full "
      fullHeight
      headerClassName="border-b px-4 py-4 md:px-6"
      onOpenChange={onOpenChange}
      open={open}
      title={t("cart.configuration.addons.modal.title")}
      titleClassName="font-medium text-2xl text-brand-green2-300"
    >
      <DietAddonEditorBody
        addon={addon}
        allowedDates={allowedDates}
        cateringName={cateringName}
        allowedKeys={allowedKeys}
        calendarLocale={calendarLocale}
        canAddMore={canAddMore}
        isAllSelected={isAllSelected}
        onDecrementDay={decrementDay}
        onIncrementDay={incrementDay}
        onRemoveDay={removeDay}
        onSyncSelectedDates={syncSelectedDates}
        onToggleAll={toggleAll}
        selectedDates={selectedDates}
        selection={selection}
      />
    </ResponsiveDialog>
  );
};
